import type { DocsLocale } from '@/lib/docs'

/**
 * Localized chrome copy for the `/learn` surface.
 *
 * Centralized here (a plain, server-importable data module — NO `'use client'`)
 * so the `/translate` editor catalog (`@/lib/i18n/learnRouteMessages`) reads the
 * exact same strings the pages render. Importing these tables out of the client
 * page components instead would hand server code a client-reference proxy, not
 * the data, so the catalog could never match rendered text. Keep this file free
 * of client-only imports.
 */

export interface LearnIndexGroupCopy {
  label: string
  description: string
}

export interface LearnIndexCopy {
  title: string
  intro: string
  docs: string
  docsDescription: string
  tutorials: string
  tutorialsDescription: string
  skills: string
  skillsDescription: string
  browseTitle: string
  community: string
  quickLinks: string
  keyboardShortcuts: string
  keyboardShortcutsDescription: string
  glossary: string
  glossaryDescription: string
  agentSetup: string
  agentSetupDescription: string
  viewAll: string
  openGuide: string
  pageSingular: string
  pagePlural: string
  suggestTitle: string
  suggestDescription: string
  suggestButton: string
  groups: Record<string, LearnIndexGroupCopy>
}

export const LEARN_INDEX_COPY: Record<DocsLocale, LearnIndexCopy> = {
  en: {
    title: 'Learn Martini',
    intro: 'Reference docs and video lessons for shot creation, project organization, and exporting your film.',
    docs: 'Docs',
    docsDescription: 'Written guides for every part of Martini — canvas, generation, sets, timeline, and export.',
    tutorials: 'Tutorials',
    tutorialsDescription: 'Short video lessons that walk through the workflow, from first shot to final cut.',
    skills: 'Skills',
    skillsDescription: 'Agent skills that extend Martini — like bringing a Blender shot into a project.',
    browseTitle: 'Browse by topic',
    community: 'From the Community',
    quickLinks: 'Quick Links',
    keyboardShortcuts: 'Keyboard Shortcuts',
    keyboardShortcutsDescription: 'Every canvas shortcut, grouped by workflow.',
    glossary: 'Glossary',
    glossaryDescription: 'Plain-language definitions for Martini terms.',
    agentSetup: 'Agent Setup',
    agentSetupDescription:
      'Use MCP to connect Claude Desktop/Web, Claude Code, Cursor, Hermes Agent, OpenClaw, and other clients to Martini.',
    viewAll: 'View all',
    openGuide: 'Open guide',
    pageSingular: 'page',
    pagePlural: 'pages',
    suggestTitle: 'What should we cover next?',
    suggestDescription: 'Tell us which workflow, model, or production problem should become a guide.',
    suggestButton: 'Suggest Content',
    groups: {
      'Using Martini': {
        label: 'Using Martini',
        description: 'Core tools, shortcuts, and workflows.',
      },
      'Your Workspace': {
        label: 'Your Workspace',
        description: 'Team settings, account settings, and billing.',
      },
      Reference: {
        label: 'Reference',
        description: 'Glossary, shortcuts, and agent setup.',
      },
    },
  },
  ja: {
    title: 'Martiniを学ぶ',
    intro: 'ショットの作成、プロジェクトの整理、映像の書き出しのためのリファレンスドキュメントと動画レッスンです。',
    docs: 'ドキュメント',
    docsDescription:
      'キャンバス、生成、セット、タイムライン、書き出しまで、Martiniの各機能を解説するリファレンスです。',
    tutorials: 'チュートリアル',
    tutorialsDescription: '最初のショットから仕上げのカットまで、ワークフローを順に追う短い動画レッスンです。',
    skills: 'Skills',
    skillsDescription: 'Agent skills that extend Martini — like bringing a Blender shot into a project.',
    browseTitle: 'トピックから探す',
    community: 'コミュニティより',
    quickLinks: 'クイックリンク',
    keyboardShortcuts: 'キーボードショートカット',
    keyboardShortcutsDescription: 'キャンバスのショートカットをワークフロー別に確認できます。',
    glossary: '用語集',
    glossaryDescription: 'Martiniで使う用語を平易に説明します。',
    agentSetup: 'エージェント設定',
    agentSetupDescription: 'MCP対応エージェントをMartiniプロジェクトに接続します。',
    viewAll: 'すべて見る',
    openGuide: 'ガイドを開く',
    pageSingular: 'ページ',
    pagePlural: 'ページ',
    suggestTitle: '次に何を取り上げるべきですか？',
    suggestDescription: 'ガイドにしてほしいワークフロー、モデル、制作上の課題を教えてください。',
    suggestButton: '内容を提案',
    groups: {
      'Using Martini': {
        label: 'Martiniを使う',
        description: '主要ツール、ショートカット、ワークフロー。',
      },
      'Your Workspace': {
        label: 'ワークスペース',
        description: 'チーム設定、アカウント設定、請求。',
      },
      Reference: {
        label: 'リファレンス',
        description: '用語集、ショートカット、エージェント設定。',
      },
    },
  },
  ar: {
    title: 'تعلّم Martini',
    intro: 'وثائق مرجعية ودروس فيديو لإنشاء اللقطات وتنظيم المشاريع وتصدير فيلمك.',
    docs: 'المستندات',
    docsDescription: 'أدلة مكتوبة لكل جزء من Martini — الكانفاس والتوليد والمجموعات والجدول الزمني والتصدير.',
    tutorials: 'الدروس',
    tutorialsDescription: 'دروس فيديو قصيرة تشرح سير العمل خطوة بخطوة، من اللقطة الأولى إلى المونتاج النهائي.',
    skills: 'Skills',
    skillsDescription: 'Agent skills that extend Martini — like bringing a Blender shot into a project.',
    browseTitle: 'تصفّح حسب الموضوع',
    community: 'من المجتمع',
    quickLinks: 'روابط سريعة',
    keyboardShortcuts: 'اختصارات لوحة المفاتيح',
    keyboardShortcutsDescription: 'كل اختصارات الكانفاس مصنّفة حسب سير العمل.',
    glossary: 'المصطلحات',
    glossaryDescription: 'تعريفات بلغة بسيطة لمصطلحات Martini.',
    agentSetup: 'إعداد العميل',
    agentSetupDescription: 'اربط عميلاً متوافقاً مع MCP بمشروع Martini.',
    viewAll: 'عرض الكل',
    openGuide: 'فتح الدليل',
    pageSingular: 'صفحة',
    pagePlural: 'صفحات',
    suggestTitle: 'ما الذي يجب أن نغطّيه بعد ذلك؟',
    suggestDescription: 'أخبرنا بسير العمل أو النموذج أو مشكلة الإنتاج التي تستحق دليلاً.',
    suggestButton: 'اقترح محتوى',
    groups: {
      'Using Martini': {
        label: 'استخدام Martini',
        description: 'الأدوات والاختصارات وسير العمل الأساسي.',
      },
      'Your Workspace': {
        label: 'مساحة عملك',
        description: 'إعدادات الفريق والحساب والفوترة.',
      },
      Reference: {
        label: 'المرجع',
        description: 'المصطلحات والاختصارات وإعداد العميل.',
      },
    },
  },
  es: {
    title: 'Aprende Martini',
    intro:
      'Documentos de referencia y lecciones en video para la creación de tomas, la organización de proyectos y la exportación de tu película.',
    docs: 'Docs',
    docsDescription:
      'Guías escritas para cada parte de Martini: lienzo, generación, sets, línea de tiempo y exportación.',
    tutorials: 'Tutoriales',
    tutorialsDescription:
      'Lecciones en video cortas que recorren el flujo de trabajo, desde la primera toma hasta el corte final.',
    skills: 'Skills',
    skillsDescription: 'Agent skills that extend Martini — like bringing a Blender shot into a project.',
    browseTitle: 'Explora por tema',
    community: 'De la comunidad',
    quickLinks: 'Enlaces rápidos',
    keyboardShortcuts: 'Atajos de teclado',
    keyboardShortcutsDescription: 'Todos los atajos del lienzo, agrupados por flujo de trabajo.',
    glossary: 'Glosario',
    glossaryDescription: 'Definiciones en lenguaje claro para los términos de Martini.',
    agentSetup: 'Configuración de agentes',
    agentSetupDescription: 'Conecta un agente compatible con MCP a un proyecto de Martini.',
    viewAll: 'Ver todo',
    openGuide: 'Abrir guía',
    pageSingular: 'página',
    pagePlural: 'páginas',
    suggestTitle: '¿Qué deberíamos cubrir a continuación?',
    suggestDescription:
      'Cuéntanos qué flujo de trabajo, modelo o problema de producción debería convertirse en una guía.',
    suggestButton: 'Sugerir contenido',
    groups: {
      'Using Martini': {
        label: 'Usar Martini',
        description: 'Herramientas principales, atajos y flujos de trabajo.',
      },
      'Your Workspace': {
        label: 'Tu espacio de trabajo',
        description: 'Ajustes de equipo, ajustes de cuenta y facturación.',
      },
      Reference: {
        label: 'Referencia',
        description: 'Glosario, atajos y configuración de agentes.',
      },
    },
  },
  ko: {
    title: 'Martini 학습',
    intro: '샷 제작, 프로젝트 정리, 영상 내보내기를 위한 레퍼런스 문서와 동영상 레슨입니다.',
    docs: '문서',
    docsDescription: '캔버스, 생성, 세트, 타임라인, 내보내기까지 Martini의 모든 부분을 설명하는 레퍼런스입니다.',
    tutorials: '튜토리얼',
    tutorialsDescription: '첫 샷부터 최종 컷까지 워크플로를 차근차근 따라가는 짧은 동영상 레슨입니다.',
    skills: 'Skills',
    skillsDescription: 'Agent skills that extend Martini — like bringing a Blender shot into a project.',
    browseTitle: '주제별로 둘러보기',
    community: '커뮤니티에서',
    quickLinks: '빠른 링크',
    keyboardShortcuts: '키보드 단축키',
    keyboardShortcutsDescription: '워크플로별로 정리된 캔버스 단축키 전체.',
    glossary: '용어집',
    glossaryDescription: 'Martini 용어의 쉬운 설명.',
    agentSetup: '에이전트 설정',
    agentSetupDescription: 'MCP 호환 에이전트를 Martini 프로젝트에 연결합니다.',
    viewAll: '전체 보기',
    openGuide: '가이드 열기',
    pageSingular: '페이지',
    pagePlural: '페이지',
    suggestTitle: '다음에 어떤 내용을 다룰까요?',
    suggestDescription: '가이드로 만들면 좋을 워크플로, 모델, 제작 문제를 알려 주세요.',
    suggestButton: '콘텐츠 제안',
    groups: {
      'Using Martini': {
        label: 'Martini 사용하기',
        description: '핵심 도구, 단축키, 워크플로.',
      },
      'Your Workspace': {
        label: '내 워크스페이스',
        description: '팀 설정, 계정 설정, 결제.',
      },
      Reference: {
        label: '레퍼런스',
        description: '용어집, 단축키, 에이전트 설정.',
      },
    },
  },
}

export interface LessonPageCopy {
  videos: string
  lesson: string
  previous: string
  next: string
  community: string
}

export const LESSON_PAGE_COPY: Record<DocsLocale, LessonPageCopy> = {
  en: {
    videos: 'Videos',
    lesson: 'Lesson',
    previous: 'Previous',
    next: 'Next',
    community: 'Community',
  },
  ja: {
    videos: '動画',
    lesson: 'レッスン',
    previous: '前へ',
    next: '次へ',
    community: 'コミュニティ',
  },
  ar: {
    videos: 'الفيديوهات',
    lesson: 'الدرس',
    previous: 'السابق',
    next: 'التالي',
    community: 'المجتمع',
  },
  es: {
    videos: 'Videos',
    lesson: 'Lección',
    previous: 'Anterior',
    next: 'Siguiente',
    community: 'Comunidad',
  },
  ko: {
    videos: '동영상',
    lesson: '레슨',
    previous: '이전',
    next: '다음',
    community: '커뮤니티',
  },
}

export interface CommunityPageCopy {
  breadcrumb: string
  title: string
  intro: string
}

export const COMMUNITY_PAGE_COPY: Record<DocsLocale, CommunityPageCopy> = {
  en: {
    breadcrumb: 'Community',
    title: 'Community',
    intro: 'Videos and posts from filmmakers and creators using Martini.',
  },
  ja: {
    breadcrumb: 'コミュニティ',
    title: 'コミュニティ',
    intro: 'Martiniを使う映像制作者やクリエイターによる動画と投稿。',
  },
  // TODO: Arabic translation needs Koh review — machine-translated initial pass.
  ar: {
    breadcrumb: 'المجتمع',
    title: 'المجتمع',
    intro: 'مقاطع فيديو ومنشورات من صانعي الأفلام والمبدعين الذين يستخدمون Martini.',
  },
  ko: {
    breadcrumb: '커뮤니티',
    title: '커뮤니티',
    intro: 'Martini를 사용하는 영상 제작자와 크리에이터들의 동영상과 게시물.',
  },
  es: {
    breadcrumb: 'Comunidad',
    title: 'Comunidad',
    intro: 'Videos y publicaciones de cineastas y creadores que trabajan con Martini.',
  },
}

export interface LearnSidebarCopy {
  overview: string
  videoLessons: string
  communityVideos: string
  keyboardShortcuts: string
  glossary: string
  agentSetup: string
  mcpSetup: string
  groups: Record<string, string>
}

export const LEARN_SIDEBAR_COPY: Record<DocsLocale, LearnSidebarCopy> = {
  en: {
    overview: 'Overview',
    videoLessons: 'Video lessons',
    communityVideos: 'Community Videos',
    keyboardShortcuts: 'Keyboard shortcuts',
    glossary: 'Glossary',
    agentSetup: 'Agent setup',
    mcpSetup: 'MCP setup',
    groups: {
      'Using Martini': 'Using Martini',
      'Your Workspace': 'Your Workspace',
      Developers: 'Developers',
      Reference: 'Reference',
    },
  },
  ja: {
    overview: '概要',
    videoLessons: '動画レッスン',
    communityVideos: 'コミュニティ動画',
    keyboardShortcuts: 'キーボードショートカット',
    glossary: '用語集',
    agentSetup: 'エージェント設定',
    mcpSetup: 'MCPセットアップ',
    groups: {
      'Using Martini': 'Martiniを使う',
      'Your Workspace': 'ワークスペース',
      Developers: '開発者向け',
      Reference: 'リファレンス',
    },
  },
  // TODO: Arabic copy needs Koh review — machine-translated initial pass.
  ar: {
    overview: 'نظرة عامة',
    videoLessons: 'دروس الفيديو',
    communityVideos: 'فيديوهات المجتمع',
    keyboardShortcuts: 'اختصارات لوحة المفاتيح',
    glossary: 'المصطلحات',
    agentSetup: 'إعداد العميل',
    mcpSetup: 'إعداد MCP',
    groups: {
      'Using Martini': 'استخدام Martini',
      'Your Workspace': 'مساحة عملك',
      Developers: 'للمطورين',
      Reference: 'المرجع',
    },
  },
  es: {
    overview: 'Resumen',
    videoLessons: 'Lecciones en video',
    communityVideos: 'Videos de la comunidad',
    keyboardShortcuts: 'Atajos de teclado',
    glossary: 'Glosario',
    agentSetup: 'Configuración de agentes',
    mcpSetup: 'Configuración de MCP',
    groups: {
      'Using Martini': 'Usar Martini',
      'Your Workspace': 'Tu espacio de trabajo',
      Developers: 'Desarrolladores',
      Reference: 'Referencia',
    },
  },
  ko: {
    overview: '개요',
    videoLessons: '동영상 레슨',
    communityVideos: '커뮤니티 동영상',
    keyboardShortcuts: '키보드 단축키',
    glossary: '용어집',
    agentSetup: '에이전트 설정',
    mcpSetup: 'MCP 설정',
    groups: {
      'Using Martini': 'Martini 사용하기',
      'Your Workspace': '내 워크스페이스',
      Developers: '개발자',
      Reference: '레퍼런스',
    },
  },
}
